import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LibraryAddCheckOutlinedIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import {
  Box,
  Button as MuiButton,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { useState } from "react";

const ERROR_COLOR = "error.main";

interface BenefitsClassToolbarProps {
  selectedCount: number;
  onDelete: VoidFunction;
  onClearCoverages: VoidFunction;
  onAssignAllCoverages: VoidFunction;
  onCopy: VoidFunction;
  onClearSelection: VoidFunction;
  onAddClass: VoidFunction;
  onAssignAllCoveragesForAllRows: VoidFunction;
  onClearCoveragesForAllRows: VoidFunction;
  classesWithoutCoverages: string[];
}

const BenefitsClassToolbar = ({
  selectedCount,
  onDelete,
  onClearCoverages,
  onAssignAllCoverages,
  onCopy,
  onClearSelection,
  onAddClass,
  onAssignAllCoveragesForAllRows,
  onClearCoveragesForAllRows,
  classesWithoutCoverages,
}: BenefitsClassToolbarProps): React.JSX.Element => {
  const [coveragesAnchorEl, setCoveragesAnchorEl] =
    useState<null | HTMLElement>(null);
  const coveragesMenuOpen = Boolean(coveragesAnchorEl);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);

  const handleMoreClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = (): void => {
    setMoreAnchorEl(null);
  };
  const hasValidationError = classesWithoutCoverages.length > 0;
  const hasSelection = selectedCount > 0;

  const handleCoveragesClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setCoveragesAnchorEl(event.currentTarget);
  };

  const handleCoveragesClose = (): void => {
    setCoveragesAnchorEl(null);
  };

  const handleClearCoverages = (): void => {
    onClearCoverages();
    handleCoveragesClose();
  };

  const handleAssignAllCoverages = (): void => {
    onAssignAllCoverages();
    handleCoveragesClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: 2,
        px: 2,
        py: 1,
        borderRadius: hasSelection ? "4px" : 0,
        backgroundColor: hasSelection ? "#F5F5F5" : "transparent",
        mb: 2,
      }}
    >
      {hasValidationError ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: ERROR_COLOR,
          }}
        >
          <ErrorOutlineIcon color="error" />
          <Typography variant="body-medium" color="danger">
            <strong>All classes require at least one coverage assigned</strong>
          </Typography>
        </Box>
      ) : hasSelection ? (
        <>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* TODO(ds-button): needs manual review — restyles color via sx to a hardcoded purple the DS variants don't offer */}
            <MuiButton
              variant="outlined"
              size="small"
              endIcon={<ClearOutlinedIcon />}
              onClick={onClearSelection}
              sx={{
                color: "#9c27b0",
                borderColor: "#9c27b0",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#7b1fa2",
                  backgroundColor: "rgba(156, 39, 176, 0.04)",
                },
              }}
            >
              {selectedCount} {selectedCount === 1 ? "Class" : "Classes"}{" "}
              Selected
            </MuiButton>
            <Button
              variant="tertiary"
              size="sm"
              startIcon={<LibraryAddCheckOutlinedIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleCoveragesClick}
            >
              Coverages
            </Button>
            <Menu
              anchorEl={coveragesAnchorEl}
              open={coveragesMenuOpen}
              onClose={handleCoveragesClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <MenuItem onClick={handleClearCoverages}>
                Clear Assigned Coverages
              </MenuItem>
              <MenuItem onClick={handleAssignAllCoverages}>
                Assign All Coverages
              </MenuItem>
            </Menu>
            <Button
              variant="tertiary"
              size="sm"
              startIcon={<ContentCopyIcon />}
              onClick={onCopy}
            >
              Copy
            </Button>
          </Box>
          <IconButton
            size="small"
            onClick={onDelete}
            title="Delete selected classes"
            sx={{ color: ERROR_COLOR }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={onAddClass} size="small">
              <AddOutlinedIcon />
            </IconButton>
            <IconButton
              id="class-manager-more-button"
              aria-label="More actions"
              aria-controls={
                moreMenuOpen ? "class-manager-more-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={moreMenuOpen ? "true" : undefined}
              onClick={handleMoreClick}
              size="small"
            >
              <MoreHorizOutlinedIcon />
            </IconButton>
            <Menu
              id="class-manager-more-menu"
              anchorEl={moreAnchorEl}
              open={moreMenuOpen}
              onClose={handleMoreClose}
              MenuListProps={{
                "aria-labelledby": "class-manager-more-button",
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  onAssignAllCoveragesForAllRows();
                  handleMoreClose();
                }}
              >
                Assign All Coverages
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onClearCoveragesForAllRows();
                  handleMoreClose();
                }}
              >
                Clear All Coverages
              </MenuItem>
            </Menu>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BenefitsClassToolbar;
